// Phase L5 — Custom Widget Plugin loader.
//
// At app boot, `bootstrapPluginWidgets()` is called once with the active token
// and (optionally) the current workspace_id. It:
//   1. Fetches GET /plugins/active/case-widgets to learn which plugins ship
//      Case Client widgets and which of those are pinned for the workspace.
//   2. Exposes a small `window.xavvy` host bridge so plugin bundles can
//      register widgets via `window.xavvy.registerWidget({...})` without
//      bundling React or our internal modules.
//   3. Inserts a <script> tag for each plugin's `bundle_url`. The bundle is a
//      plain JS file (`widgets.js` at the plugin's install root) served by
//      the backend; it self-registers on load.
//
// The renderer's WidgetErrorBoundary already paints a "widget unavailable"
// placeholder when a layout references an unknown widget type — so a plugin
// that fails to load (network error, syntax error, blocked by governance) is
// non-fatal: the rest of the layout still renders.

import React from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { registerWidget, getWidget } from './widgetRegistry';
import { useLayoutCtx, useWidgetActions } from './LayoutContext';

const _loadedBundles = new Set();   // node_id values whose <script> tag has been added
const _pluginIndex = new Map();     // type → { plugin_id, node_id, name, icon }

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function ensureHostBridge() {
  if (typeof window === 'undefined') return;
  if (window.xavvy && typeof window.xavvy.registerWidget === 'function') return;
  // Plugin bundles see a stable global. They can rely on `window.xavvy.React`
  // for JSX runtime needs (so they don't need to bundle React themselves) and
  // on `window.xavvy.registerWidget(meta)` to add widgets to our registry.
  window.xavvy = Object.assign(window.xavvy || {}, {
    React,
    registerWidget(meta) {
      if (!meta || typeof meta !== 'object' || !meta.type) {
        if (typeof console !== 'undefined') console.warn('[plugin-widget] invalid registerWidget call', meta);
        return null;
      }
      const indexEntry = window.xavvy._currentPlugin || null;
      const enriched = {
        ...meta,
        // Tag every plugin widget so the Designer palette can group them and
        // the renderer can render the plugin's icon / name in error fallbacks.
        plugin_node_id: indexEntry?.node_id || meta.plugin_node_id || null,
        plugin_name: indexEntry?.name || meta.plugin_name || null,
        plugin_icon: indexEntry?.icon || meta.plugin_icon || null,
        group: meta.group || (indexEntry?.name ? `Plugins: ${indexEntry.name}` : 'Plugins'),
      };
      const reg = registerWidget(enriched);
      _pluginIndex.set(meta.type, {
        plugin_id: indexEntry?.plugin_id || null,
        node_id: indexEntry?.node_id || null,
        name: indexEntry?.name || null,
        icon: indexEntry?.icon || null,
      });
      return reg;
    },
    // Plugin bundles can read this if they want to defer registration while
    // resources load asynchronously (e.g. waiting on a font or icon set).
    getWidget,
    // Hooks plugin bundles call from inside their Component to read the live
    // layout context (case, work item, draft, bus, callbacks) and to register
    // imperative actions for the wire executor. Exposed as host bridge so the
    // bundle never bundles React or our internal modules.
    useLayoutCtx,
    useWidgetActions,
  });
}

function loadBundle(plugin) {
  return new Promise((resolve) => {
    if (!plugin?.node_id || _loadedBundles.has(plugin.node_id)) {
      resolve({ ok: true, alreadyLoaded: true });
      return;
    }
    if (!plugin.bundle_url) {
      resolve({ ok: false, error: 'No bundle_url for plugin' });
      return;
    }
    if (typeof document === 'undefined') {
      resolve({ ok: false, error: 'No document — SSR?' });
      return;
    }
    _loadedBundles.add(plugin.node_id);
    // Set the "current plugin" context so registerWidget calls can attribute
    // the widget to its source plugin without each bundle having to repeat
    // identity metadata.
    if (typeof window !== 'undefined') {
      window.xavvy = window.xavvy || {};
      window.xavvy._currentPlugin = {
        plugin_id: plugin.plugin_id,
        node_id: plugin.node_id,
        name: plugin.name,
        icon: plugin.icon,
      };
    }
    const script = document.createElement('script');
    // Backend bundle URL is relative to API_BASE_URL.
    script.src = `${API_BASE_URL}${plugin.bundle_url}`;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      if (typeof window !== 'undefined') window.xavvy._currentPlugin = null;
      resolve({ ok: true, node_id: plugin.node_id });
    };
    script.onerror = (err) => {
      if (typeof window !== 'undefined') window.xavvy._currentPlugin = null;
      if (typeof console !== 'undefined') {
        console.warn(`[plugin-widget] failed to load ${plugin.node_id}: ${plugin.bundle_url}`, err);
      }
      _loadedBundles.delete(plugin.node_id); // allow a retry on next bootstrap
      resolve({ ok: false, error: 'load_error', node_id: plugin.node_id });
    };
    document.head.appendChild(script);
  });
}

/**
 * Fetch active plugin manifests + load each bundle. Idempotent — calling
 * twice with the same workspace + already-loaded plugins is a no-op.
 *
 * Returns a Promise resolving to an array of { node_id, ok, error? } so
 * callers can surface load failures to the user if they care.
 */
export async function bootstrapPluginWidgets({ token, workspaceId } = {}) {
  ensureHostBridge();
  if (!token) return [];
  const params = workspaceId ? { workspace_id: workspaceId } : {};
  let manifests = [];
  try {
    const res = await axios.get(`${API_BASE_URL}/plugins/active/case-widgets`, {
      params,
      headers: authHeaders(token),
    });
    manifests = Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    if (typeof console !== 'undefined') {
      console.warn('[plugin-widget] failed to fetch /plugins/active/case-widgets', e?.message || e);
    }
    return [];
  }
  const results = await Promise.all(manifests.map(loadBundle));
  return results;
}

/**
 * Look up plugin metadata for a registered widget type. Returns null if the
 * type is built-in or the plugin metadata is unknown.
 */
export function getPluginForWidget(type) {
  return _pluginIndex.get(type) || null;
}

/**
 * Used by tests — exposes the in-memory plugin index without needing to mock
 * the registry. Not part of the runtime contract.
 */
export function _dumpPluginIndex() {
  return Array.from(_pluginIndex.entries()).map(([type, meta]) => ({ type, ...meta }));
}
